import { createContext, useContext, useRef } from "react";
import { createStore, useStore } from "zustand";

import { logError } from "@/utils/logger";

import {
  claimAudio,
  knownDuration,
  loadWaveform,
  preparedAudioUrl,
  releaseAudio,
} from "../hooks/useAudioPlayer";
import { scrollToMessageElement } from "../utils/message";

export interface ActiveVoice {
  messageId: number;
  url: string;
  userName: string;
  userPosition?: string | null;
}

type ScrollToMessage = (messageId: number) => void | Promise<void>;

export interface VoicePlaybackState {
  active: ActiveVoice | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  rate: number;
  playVoice: (meta: ActiveVoice) => void;
  closeVoice: () => void;
  toggle: () => void;
  seek: (ratio: number) => void;
  setRate: (rate: number) => void;
  setScrollToMessage: (fn: ScrollToMessage | null) => void;
  scrollToActiveVoice: () => void;
  bind: () => () => void;
}

const initialPlayback = {
  active: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  progress: 0,
  rate: 1,
};

export const createVoicePlaybackStore = () => {
  const audio = new Audio();
  audio.preload = "auto";
  audio.preservesPitch = true;

  let loadedUrl: string | null = null;
  let scrollToMessage: ScrollToMessage | null = null;
  let frame = 0;
  let stopWaveform = () => {};

  const store = createStore<VoicePlaybackState>((set, get) => {
    const publishClock = () => {
      const url = loadedUrl;
      if (!url) return;
      const duration = knownDuration(url, audio);
      const currentTime = audio.currentTime;
      set({
        isPlaying: !audio.paused,
        currentTime,
        duration,
        progress: duration > 0 ? Math.min(1, currentTime / duration) : 0,
      });
    };

    const stopClock = () => window.cancelAnimationFrame(frame);

    const startClock = () => {
      stopClock();
      const tick = () => {
        publishClock();
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    };

    const play = () => {
      void audio.play().catch((error: unknown) => {
        logError(error, { context: "audio_playback" });
      });
    };

    const onPlay = () => {
      claimAudio(audio);
      set({ isPlaying: true });
      startClock();
    };

    const onPause = () => {
      stopClock();
      publishClock();
    };

    const onEnded = () => {
      stopClock();
      releaseAudio(audio);
      publishClock();
    };

    return {
      ...initialPlayback,

      playVoice: (meta) => {
        set({ active: meta });
        if (loadedUrl !== meta.url) {
          loadedUrl = meta.url;
          audio.src = preparedAudioUrl(meta.url);
          audio.playbackRate = get().rate;
          stopWaveform();
          stopWaveform = loadWaveform(meta.url, () => {
            if (loadedUrl === meta.url) publishClock();
          });
        }
        play();
      },

      closeVoice: () => {
        audio.pause();
        stopClock();
        stopWaveform();
        stopWaveform = () => {};
        releaseAudio(audio);
        loadedUrl = null;
        audio.removeAttribute("src");
        audio.load();
        set({ ...initialPlayback, rate: get().rate });
      },

      toggle: () => {
        if (!get().active) return;
        if (audio.paused) play();
        else audio.pause();
      },

      seek: (ratio) => {
        const url = loadedUrl;
        if (!url) return;
        const length = knownDuration(url, audio);
        if (length <= 0) return;
        audio.currentTime = Math.max(0, Math.min(1, ratio)) * length;
        publishClock();
      },

      setRate: (next) => {
        if (next === get().rate) return;
        audio.playbackRate = next;
        set({ rate: next });
      },

      setScrollToMessage: (fn) => {
        scrollToMessage = fn;
      },

      scrollToActiveVoice: () => {
        const active = get().active;
        if (!active) return;
        if (scrollToMessage) {
          void scrollToMessage(active.messageId);
          return;
        }
        scrollToMessageElement(active.messageId);
      },

      bind: () => {
        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("loadedmetadata", publishClock);
        audio.addEventListener("durationchange", publishClock);

        return () => {
          stopClock();
          stopWaveform();
          stopWaveform = () => {};
          audio.pause();
          releaseAudio(audio);
          audio.removeEventListener("play", onPlay);
          audio.removeEventListener("pause", onPause);
          audio.removeEventListener("ended", onEnded);
          audio.removeEventListener("loadedmetadata", publishClock);
          audio.removeEventListener("durationchange", publishClock);
          loadedUrl = null;
          audio.removeAttribute("src");
          audio.load();
        };
      },
    };
  });

  return store;
};

type VoicePlaybackStore = ReturnType<typeof createVoicePlaybackStore>;

export const VoicePlaybackContext = createContext<VoicePlaybackStore | null>(
  null,
);

export function useCreateVoicePlaybackStore() {
  const store = useRef<VoicePlaybackStore>(undefined);

  if (!store.current) {
    store.current = createVoicePlaybackStore();
  }

  return store.current;
}

function useVoicePlaybackStore() {
  const store = useContext(VoicePlaybackContext);
  if (!store) {
    throw new Error(
      "useVoicePlaybackContext must be used inside VoicePlaybackProvider",
    );
  }
  return store;
}

export function useVoicePlaybackContext<T>(
  selector: (state: VoicePlaybackState) => T,
): T {
  return useStore(useVoicePlaybackStore(), selector);
}
